/**
 * OPL Issue List page - left-right split layout matching requirements management.
 * Left: scrollable issue list; Right: selected issue detail.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Empty, Pagination, Radio, Select, Space, Spin } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { IssuePriority, IssueStatus, OplIssueDetail } from '@/types';
import { useOplStore } from '@/store/oplStore';
import * as oplApi from '@/api/opl';
import IssueStatusTag from '@/components/opl/IssueStatusTag';
import PriorityTag from '@/components/opl/PriorityTag';
import UserSelector from '@/components/opl/UserSelector';
import OplIssueCard from './OplIssueCard';
import OplIssueForm from './OplIssueForm';

const MIN_LEFT = 200;
const MAX_LEFT = 480;
const DEFAULT_LEFT = 280;

interface OplIssueListProps {
  projectId: number;
  initialIssueId?: string;
}

const OplIssueList: React.FC<OplIssueListProps> = ({ projectId, initialIssueId }) => {
  const { issues, pagination, isLoading, fetchIssues } = useOplStore();
  const [, setSearchParams] = useSearchParams();
  const [filterStatus, setFilterStatus] = useState<IssueStatus | undefined>();
  const [filterPriority, setFilterPriority] = useState<IssuePriority | undefined>();
  const [filterOwner, setFilterOwner] = useState<number | null>(null);
  const [filterAffiliation, setFilterAffiliation] = useState<'momenta' | 'vendor' | undefined>();
  const [formOpen, setFormOpen] = useState(false);
  const [selectedIssueId, setSelectedIssueId] = useState<number | null>(null);
  const [selectedIssue, setSelectedIssue] = useState<OplIssueDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Draggable divider state
  const [leftWidth, setLeftWidth] = useState(DEFAULT_LEFT);
  const dragging = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const loadIssues = (page = 1) => {
    fetchIssues(projectId, {
      status: filterStatus,
      priority: filterPriority,
      owner: filterOwner,
      owner_affiliation: filterAffiliation,
      page,
      page_size: 20,
    });
  };

  useEffect(() => {
    loadIssues();
    if (!initialIssueId) {
      setSelectedIssueId(null);
      setSelectedIssue(null);
    }
  }, [projectId, filterStatus, filterPriority, filterOwner, filterAffiliation]);

  // Auto-load initial issue detail from URL param (e.g. "HNAS-Issue-106")
  useEffect(() => {
    if (!initialIssueId) return;
    const parts = initialIssueId.split('-');
    const issueNumber = Number(parts[parts.length - 1]);
    if (!issueNumber) return;
    oplApi.listIssues(projectId, { issue_number: issueNumber, page_size: 1 }).then((res) => {
      if (res.results.length > 0) {
        handleSelectIssue(res.results[0].id, initialIssueId);
      }
    });
  }, [initialIssueId]);

  const handleSelectIssue = async (issueId: number, displayId: string) => {
    if (selectedIssueId === issueId) return;
    setSelectedIssueId(issueId);
    setSearchParams({ issueId: displayId }, { replace: true });
    setDetailLoading(true);
    try {
      const detail = await oplApi.getIssue(projectId, issueId);
      setSelectedIssue(detail);
    } catch {
      setSelectedIssue(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleRefresh = () => {
    loadIssues(pagination.page);
    if (selectedIssueId) {
      oplApi
        .getIssue(projectId, selectedIssueId)
        .then(setSelectedIssue)
        .catch(() => {
          setSelectedIssueId(null);
          setSelectedIssue(null);
        });
    }
  };

  // Draggable divider handlers
  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragging.current = true;

    const onMouseMove = (ev: MouseEvent) => {
      if (!dragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const newWidth = Math.min(MAX_LEFT, Math.max(MIN_LEFT, ev.clientX - rect.left));
      setLeftWidth(newWidth);
    };

    const onMouseUp = () => {
      dragging.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  return (
    <div>
      {/* Toolbar */}
      <div className="flex justify-between items-center mb-4">
        <Space>
          <Select
            placeholder="状态筛选"
            allowClear
            value={filterStatus}
            onChange={setFilterStatus}
            style={{ width: 140 }}
            options={[
              { value: 'opened', label: '开放' },
              { value: 'closed', label: '已关闭' },
            ]}
          />
          <Select
            placeholder="优先级筛选"
            allowClear
            value={filterPriority}
            onChange={setFilterPriority}
            style={{ width: 140 }}
            options={[
              { value: 'p0', label: 'P0-紧急' },
              { value: 'p1', label: 'P1-高' },
              { value: 'p2', label: 'P2-中' },
              { value: 'p3', label: 'P3-低' },
            ]}
          />
          <UserSelector
            value={filterOwner}
            onChange={setFilterOwner}
            placeholder="负责人筛选"
            allowClear
          />
          <Radio.Group
            value={filterAffiliation}
            onChange={(e) => setFilterAffiliation(e.target.value || undefined)}
            buttonStyle="solid"
            size="small"
          >
            <Radio.Button value={undefined}>全部</Radio.Button>
            <Radio.Button value="momenta">Momenta</Radio.Button>
            <Radio.Button value="vendor">供应商</Radio.Button>
          </Radio.Group>
        </Space>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setFormOpen(true)}
        >
          新建问题
        </Button>
      </div>

      {/* Left-right split layout */}
      {isLoading ? (
        <div className="text-center py-12">
          <Spin size="large" />
        </div>
      ) : issues.length === 0 ? (
        <Empty description="暂无开口问题" />
      ) : (
        <div ref={containerRef} style={{ display: 'flex', height: 'calc(100vh - 260px)' }}>
          {/* Left panel: issue list */}
          <div
            style={{
              width: leftWidth,
              minWidth: MIN_LEFT,
              overflowY: 'auto',
              paddingRight: 8,
            }}
          >
            {issues.map((issue) => (
              <div
                key={issue.id}
                onClick={() => handleSelectIssue(issue.id, issue.issue_id)}
                style={{
                  padding: '8px 12px',
                  marginBottom: 4,
                  borderRadius: 6,
                  cursor: 'pointer',
                  background: issue.id === selectedIssueId ? '#e6f4ff' : 'transparent',
                  borderLeft: issue.id === selectedIssueId ? '3px solid #1677ff' : '3px solid transparent',
                  transition: 'background 0.2s',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>{issue.issue_id}</span>
                  <IssueStatusTag status={issue.status} />
                  {issue.priority && <PriorityTag priority={issue.priority} />}
                </div>
                {issue.description_summary && (
                  <div
                    style={{
                      fontSize: 12,
                      color: '#888',
                      marginTop: 4,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {issue.description_summary}
                  </div>
                )}
                {issue.owner && (
                  <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                    负责人: {issue.owner.display_name || issue.owner.username}
                  </div>
                )}
              </div>
            ))}

            {/* Pagination at bottom of left panel */}
            {pagination.totalPages > 1 && (
              <div style={{ padding: '12px 0', textAlign: 'center' }}>
                <Pagination
                  size="small"
                  current={pagination.page}
                  total={pagination.count}
                  pageSize={pagination.pageSize}
                  onChange={(page) => loadIssues(page)}
                  showTotal={(total) => `共 ${total} 条`}
                />
              </div>
            )}
          </div>

          {/* Draggable divider */}
          <div
            onMouseDown={onMouseDown}
            style={{
              width: 6,
              cursor: 'col-resize',
              background: '#f0f0f0',
              borderRadius: 3,
              flexShrink: 0,
              transition: 'background 0.2s',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLDivElement).style.background = '#d9d9d9';
            }}
            onMouseLeave={(e) => {
              if (!dragging.current)
                (e.currentTarget as HTMLDivElement).style.background = '#f0f0f0';
            }}
          />

          {/* Right panel: selected issue detail */}
          <div style={{ flex: 1, minWidth: 0, overflowY: 'auto', paddingLeft: 16 }}>
            {detailLoading ? (
              <div className="flex justify-center py-12">
                <Spin size="large" />
              </div>
            ) : selectedIssue ? (
              <OplIssueCard
                key={selectedIssue.id}
                issue={selectedIssue}
                projectId={projectId}
                onRefresh={handleRefresh}
              />
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 300,
                  color: '#999',
                }}
              >
                请从左侧选择一个问题查看详情
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create form */}
      <OplIssueForm
        open={formOpen}
        projectId={projectId}
        onClose={() => setFormOpen(false)}
        onSuccess={() => {
          setFormOpen(false);
          loadIssues();
        }}
      />
    </div>
  );
};

export default OplIssueList;
